import {
  API_ROUTES,
  bankTransactionSchema,
  type ConfirmMatchRequest,
  type CreateAccountRequest,
  type DismissMatchRequest,
  financialAccountSchema,
  type ImportStatementRequest,
  importSummarySchema,
  type ListBankTransactionsQuery,
  pendingStatementSchema,
  statementPreviewSchema,
  type ExplainDepositRequest,
  type ExplainPaymentRequest,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/** Bank queries. */

export const bankKeys = {
  all: (organizationId: string) => ['bank', organizationId] as const,
} as const;

export const accountsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['bank', organizationId, 'accounts'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.bank.accounts(organizationId),
        schema: z.array(financialAccountSchema),
        signal,
      }),
    staleTime: 300_000,
  });

export const pendingStatementsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['bank', organizationId, 'statements'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.bank.statements(organizationId),
        schema: z.array(pendingStatementSchema),
        signal,
      }),
  });

export const statementPreviewQuery = (organizationId: string, documentId: string) =>
  queryOptions({
    queryKey: ['bank', organizationId, 'statement', documentId] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.bank.statementPreview(organizationId, documentId),
        schema: statementPreviewSchema,
        signal,
      }),
  });

export const bankTransactionsQuery = (
  organizationId: string,
  query: ListBankTransactionsQuery = {},
) =>
  queryOptions({
    queryKey: ['bank', organizationId, 'transactions', query] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (query.state !== undefined) params.set('state', query.state);
      if (query.limit !== undefined) params.set('limit', String(query.limit));
      const search = params.toString();
      return apiClient.request({
        path: `${API_ROUTES.bank.transactions(organizationId)}${search === '' ? '' : `?${search}`}`,
        schema: z.array(bankTransactionSchema),
        signal,
      });
    },
  });

export const createAccount = (organizationId: string, body: CreateAccountRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bank.accounts(organizationId),
    schema: financialAccountSchema,
    body,
  });

export const importStatement = (
  organizationId: string,
  documentId: string,
  body: ImportStatementRequest,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bank.statementImport(organizationId, documentId),
    schema: importSummarySchema,
    body,
  });

/** Read a PDF statement again, after a reading that did not prove. */
export const rereadStatement = (organizationId: string, documentId: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bank.statementReread(organizationId, documentId),
    schema: z.undefined(),
  });

export const confirmMatch = (
  organizationId: string,
  bankTransactionId: string,
  body: ConfirmMatchRequest,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bank.confirm(organizationId, bankTransactionId),
    schema: z.undefined(),
    body,
  });

export const dismissMatch = (
  organizationId: string,
  bankTransactionId: string,
  body: DismissMatchRequest,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bank.dismiss(organizationId, bankTransactionId),
    schema: z.undefined(),
    body,
  });

/** Looks again for the records behind every waiting line. */
export const rematchBankLines = (organizationId: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bank.rematch(organizationId),
    schema: z.object({ proposed: z.number() }),
    body: {},
  });

/** Says what money leaving the account was: net pay, a remittance, a transfer, a loan payment, a draw. */
export const explainPayment = (
  organizationId: string,
  bankTransactionId: string,
  body: ExplainPaymentRequest,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bank.explainPayment(organizationId, bankTransactionId),
    schema: z.undefined(),
    body,
  });

/** Says what a deposit was. A customer payment becomes a draft income record. */
export const explainDeposit = (
  organizationId: string,
  bankTransactionId: string,
  body: ExplainDepositRequest,
) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.bank.explain(organizationId, bankTransactionId),
    schema: z.object({ recordId: z.string().nullable() }),
    body,
  });
